import React, { useEffect, useState } from 'react';
import { BackIcon } from './icons';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { labelAgendaCache } from '../utils/viewCache';
import EventCard from './EventCard';
import EventIcon, { ICONS, iconNames, aliasesFor } from './EventIcon';

// Everything still to come under one label. Opened by tapping a label on an
// event: the question a label really asks is "when do I have the gym?", and
// until now it could only answer "today".
// Labels are shown in their own colour, so the page is recognisable at a glance
// even before you read the title.
const L = {
  it: { next: "Prossimo", back: 'Indietro', empty: 'Nessun impegno in programma con questa etichetta.', today: 'Oggi', tomorrow: 'Domani', error: 'Impossibile caricare gli impegni.', retry: 'Riprova', count: n => `${n} in programma` },
  en: { next: "Next", back: 'Back', empty: 'Nothing coming up with this label.', today: 'Today', tomorrow: 'Tomorrow', error: "Couldn't load your appointments.", retry: 'Retry', count: n => `${n} coming up` },
  es: { next: "Próximo", back: 'Atrás', empty: 'No hay nada previsto con esta etiqueta.', today: 'Hoy', tomorrow: 'Mañana', error: 'No se pudieron cargar las citas.', retry: 'Reintentar', count: n => `${n} previstos` },
  de: { next: "Nächster", back: 'Zurück', empty: 'Nichts Anstehendes mit diesem Label.', today: 'Heute', tomorrow: 'Morgen', error: 'Termine konnten nicht geladen werden.', retry: 'Erneut versuchen', count: n => `${n} anstehend` },
  fr: { next: "Prochain", back: 'Retour', empty: 'Rien de prévu avec cette étiquette.', today: "Aujourd'hui", tomorrow: 'Demain', error: 'Impossible de charger les événements.', retry: 'Réessayer', count: n => `${n} à venir` },
  nl: { next: "Volgende", back: 'Terug', empty: 'Niets gepland met dit label.', today: 'Vandaag', tomorrow: 'Morgen', error: 'Afspraken konden niet worden geladen.', retry: 'Opnieuw', count: n => `${n} gepland` },
  pt: { next: "Próximo", back: 'Voltar', empty: 'Nada previsto com esta etiqueta.', today: 'Hoje', tomorrow: 'Amanhã', error: 'Não foi possível carregar os eventos.', retry: 'Tentar novamente', count: n => `${n} previstos` },
  ru: { next: "Ближайшее", back: 'Назад', empty: 'С этой меткой ничего не запланировано.', today: 'Сегодня', tomorrow: 'Завтра', error: 'Не удалось загрузить события.', retry: 'Повторить', count: n => `запланировано: ${n}` },
  pl: { next: "Najbliższe", back: 'Wstecz', empty: 'Nic nie zaplanowano z tą etykietą.', today: 'Dziś', tomorrow: 'Jutro', error: 'Nie można załadować wydarzeń.', retry: 'Spróbuj ponownie', count: n => `zaplanowane: ${n}` },
  ro: { next: "Următorul", back: 'Înapoi', empty: 'Nimic programat cu această etichetă.', today: 'Azi', tomorrow: 'Mâine', error: 'Nu s-au putut încărca evenimentele.', retry: 'Reîncearcă', count: n => `${n} programate` },
  zh: { next: "最近", back: '返回', empty: '该标签暂无安排。', today: '今天', tomorrow: '明天', error: '无法加载日程。', retry: '重试', count: n => `共 ${n} 项` },
  ja: { next: "次の予定", back: '戻る', empty: 'このラベルの予定はありません。', today: '今日', tomorrow: '明日', error: '予定を読み込めませんでした。', retry: '再試行', count: n => `${n} 件の予定` },
  hi: { next: "अगला", back: 'वापस', empty: 'इस लेबल के साथ कोई आगामी कार्यक्रम नहीं।', today: 'आज', tomorrow: 'कल', error: 'इवेंट लोड नहीं हो सके।', retry: 'पुनः प्रयास', count: n => `${n} आगामी` },
  ko: { next: "다음", back: '뒤로', empty: '이 라벨로 예정된 일정이 없습니다.', today: '오늘', tomorrow: '내일', error: '일정을 불러오지 못했습니다.', retry: '다시 시도', count: n => `${n}건 예정` },
  ar: { next: "التالي", back: 'رجوع', empty: 'لا توجد مواعيد قادمة بهذه العلامة.', today: 'اليوم', tomorrow: 'غدًا', error: 'تعذّر تحميل المواعيد.', retry: 'إعادة المحاولة', count: n => `${n} قادمة` },
};

// The day an event belongs to. An all-day event arrives as a plain
// 'YYYY-MM-DD': putting that through new Date() reads it as UTC midnight, which
// lands on the day before for anyone west of Greenwich. Take it as written.
function localDay(d, allDay) {
  const s = String(d || '');
  if (allDay && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  try { return new Date(s).toLocaleDateString('en-CA'); } catch (_) { return ''; }
}

// `shared` switches which question is being asked: a label's agenda, or a
// shared calendar's. Everything below is identical either way, and that is the
// point — Silvia asked for the shared calendar to work "con le stesse regole e
// struttura" as the labels, and the cheapest way to keep two things identical
// is for them to be one thing.
export default function LabelAgenda({ shared, icon }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const lang = user?.language || 'it';
  const base = lang.split('-')[0];
  const t = L[base] || L.en;

  // Start from what we already know, if anything: this same list was just
  // fetched by the "coming up" strip in Today, and asking the provider for a
  // whole year again means several seconds of spinner for an answer we have.
  // The page paints at once and quietly refreshes underneath.
  // The two kinds share one cache, so the key has to say which is which: a
  // label and a calendar could otherwise collide on the same id.
  const cacheKey = (icon ? 'i:' : shared ? 'g:' : 'l:') + id;
  // For an icon the id in the address IS the character. What it is called comes
  // from the app's own list, in the language being read — the server has no
  // business knowing that 🎂 is "Compleanno" in Italian.
  const theIcon = icon ? decodeURIComponent(id) : '';
  const iconName = icon
    ? (iconNames(lang)[ICONS.indexOf(theIcon)] || '')
    : '';
  const [data, setData] = useState(() => labelAgendaCache.get(cacheKey) || null);
  const [state, setState] = useState(() => (labelAgendaCache.has(cacheKey) ? 'ready' : 'loading'));
  const [attempt, setAttempt] = useState(0);     // bumped by "Retry"

  useEffect(() => {
    let cancelled = false;
    const cached = labelAgendaCache.get(cacheKey);
    setData(cached || null);
    setState(cached ? 'ready' : 'loading');
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    apiClient.get(shared ? `/groups/${id}/upcoming` : '/events/upcoming',
      { params: shared ? { tz, days: 365 }
        : icon ? { icons: aliasesFor(theIcon).join(','), tz, days: 365 }
        : { subCalendarId: id, tz, days: 365 } })
      .then((res) => {
        if (cancelled) return;
        labelAgendaCache.set(cacheKey, res.data);
        setData(res.data);
        setState('ready');
      })
      // A failed refresh must not wipe a list that is already on screen.
      .catch(() => { if (!cancelled && !cached) setState('error'); });
    return () => { cancelled = true; };
  }, [id, attempt, shared, icon]);

  const label = data?.label;
  const events = data?.events || [];

  // Deleting from here must empty the row here too, exactly as it does in Today.
  function removeEvent(deletedId) {
    setData(d => (d ? { ...d, events: (d.events || []).filter(e => e.id !== deletedId) } : d));
  }

  const todayKey = localDay(new Date());
  const tomorrowKey = localDay(new Date(Date.now() + 86400000));

  // Group by calendar day, keeping the order the server sent (already by time).
  // Events keep their REAL day. Filing an already-running event under today
  // looked tidy in theory and was disorienting in practice: with one long
  // holiday in the list, every heading on the page said "Today".
  // What it needed was not a different heading but the missing fact, and the
  // card now carries it: "→ 16 Aug".
  const days = [];
  events.forEach((ev) => {
    const key = localDay(ev.start, ev.allDay);
    const last = days[days.length - 1];
    if (last && last.key === key) last.items.push(ev);
    else days.push({ key, items: [ev] });
  });
  // The one that happens soonest. The list arrives sorted, so it is simply
  // the first — but naming it here is what lets the card say so.
  const nextId = events.length ? events[0].id : null;

  function dayTitle(key) {
    if (key === todayKey) return t.today;
    if (key === tomorrowKey) return t.tomorrow;
    try {
      return new Date(key + 'T12:00:00').toLocaleDateString(lang, {
        weekday: 'long', day: 'numeric', month: 'long',
      });
    } catch (_) { return key; }
  }

  // This page is built out of the SAME pieces as the rest of the app: the title
  // uses .page-title, the events are real <EventCard>s and the day headings copy
  // the "Coming up" list inside Today. It used to draw its own rows — a thin
  // bordered box with the time in a fixed 52px column — so tapping a label on an
  // event landed you somewhere that looked like a different application: other
  // fonts, other sizes, no priority stripe, no location, no attachment clip, and
  // no way to share or delete without opening the event first.
  return (
    <div style={{ padding: '4px 0 24px' }}>
      {/* Back + how many are coming up */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        {/* The same green circle as everywhere else. A word in grey type was
            the only "back" in the app that did not look like the others, and a
            way out should be recognised before it is read. */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label={t.back}
          title={t.back}
          style={{
            flexShrink: 0, width: 34, height: 34, borderRadius: '50%', padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--color-primary)', color: '#fff',
            border: 'none', cursor: 'pointer',
          }}>
          <span className="rtl-flip" style={{ display: 'flex' }}><BackIcon /></span>
        </button>
        <span style={{ flex: 1 }} />
        {state === 'ready' && events.length > 0 && (
          <span style={{
            fontSize: '0.74rem', fontWeight: 800, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--color-text-muted)',
          }}>
            {t.count(events.length)}
          </span>
        )}
      </div>

      {/* The label keeps its own colour — that is what makes the page
          recognisable before you read it. The size sits halfway between a page
          title (1.2rem) and the label chip on an event (0.68rem): it is a
          heading, but a label is not a headline. Uppercase and letter-spaced
          like every other place a label name appears. */}
      {/* Name only — no emoji. The colour already says which label this is,
          and the icon was the loudest thing on a quiet page. */}
      <h2 style={{
        margin: '0 0 16px',
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: '0.95rem', fontWeight: 800,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        color: label?.color || 'var(--color-text)',
      }}>
        {/* An icon's page keeps its drawing: here the picture IS the subject,
            which is not true of a label, where the colour already says it. */}
        {icon && <EventIcon icon={theIcon} size={22} />}
        {icon ? (iconName || '…') : (label?.name || '…')}
      </h2>

      {state === 'loading' && <div className="spinner" style={{ margin: '14px auto' }} />}

      {state === 'error' && (
        <p style={{ fontSize: '0.9rem', color: '#c0392b', paddingInlineStart: 4 }}>
          {t.error}{' '}
          <button type="button" onClick={() => setAttempt(a => a + 1)}
            style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, color: 'var(--color-primary-dark)', textDecoration: 'underline' }}>
            {t.retry}
          </button>
        </p>
      )}

      {state === 'ready' && events.length === 0 && (
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', lineHeight: 1.5, paddingInlineStart: 4 }}>
          {t.empty}
        </p>
      )}

      {state === 'ready' && days.map(day => (
        <div key={day.key} style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-text-muted)',
            marginBottom: 5, paddingInlineStart: 4,
          }}>
            {dayTitle(day.key)}
          </div>
          {day.items.map(ev => (
            // No label chip here: the page IS the label, saying it on every
            // row adds nothing and crowds the card.
            //
            // The first one in the whole list is the next thing that happens,
            // and it is the reason most people open this page at all — "when
            // is my next lesson". It is marked rather than merely first,
            // because "first" is only obvious once you have read the dates.
            ev.id === nextId ? (
              <div key={ev.id} style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', top: -7, insetInlineStart: 10, zIndex: 1,
                  fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.06em',
                  textTransform: 'uppercase', color: '#fff',
                  background: 'var(--color-primary)', borderRadius: 10, padding: '2px 8px',
                }}>
                  {t.next}
                </span>
                <div style={{ boxShadow: '0 0 0 2px var(--color-primary)', borderRadius: 'var(--radius-sm, 10px)' }}>
                  <EventCard event={ev} onDelete={removeEvent} hideSubCalendar />
                </div>
              </div>
            ) : (
              <EventCard key={ev.id} event={ev} onDelete={removeEvent} hideSubCalendar />
            )
          ))}
        </div>
      ))}
    </div>
  );
}
